import type { SavedPlace, SavedPlaceKind } from '@itin/shared'
import { useMemo, useState } from 'react'
import { useT } from '../lib/i18n.js'
import { Button, Input, Select } from './ui.js'

/**
 * Finding one place in a library that has grown.
 *
 * The assumption behind the first version was that the list would be short
 * enough to read. It will not be: an advisor who has been sending people to
 * Europe for years has hundreds of these, and a scrollable list of hundreds is
 * a list she stops opening.
 *
 * So three narrowing controls that compose: free text across name, destination
 * and her own notes; the destination, offered as the set she has actually used
 * rather than a fixed vocabulary; and a coarse category. Any of them alone cuts
 * the list to something scannable, and the three together find one entry.
 */

export const SAVED_PLACE_KINDS: readonly SavedPlaceKind[] = [
  'restaurant',
  'hotel',
  'cafe',
  'bar',
  'museum',
  'shop',
  'activity',
  'other',
]

/** Accent- and case-insensitive, so `Sao` finds `São` and `paris` finds `Paris`. */
export function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

/**
 * Filters and orders the library for a given trip.
 *
 * Exported because two surfaces use it and they must agree: a place she can see
 * in the library card has to be findable from inside a day by the same words.
 */
export function narrowSavedPlaces(
  all: SavedPlace[],
  { query, destination, kind, here }: {
    query: string
    destination: string
    kind: string
    /** The trip's city. Matches sort first; they are never filtered out. */
    here: string
  },
): SavedPlace[] {
  const needle = fold(query)
  const matches = all.filter((place) => {
    if (destination && fold(place.destination) !== fold(destination)) return false
    if (kind && place.kind !== kind) return false
    if (!needle) return true
    return (
      fold(place.name).includes(needle) ||
      fold(place.destination).includes(needle) ||
      fold(place.notes ?? '').includes(needle)
    )
  })

  // Here first, elsewhere after — never a filter. She may well want the Lisbon
  // hotel on a Paris trip, and hiding it would be the product deciding for her.
  const city = fold(here)
  const isHere = (p: SavedPlace) =>
    city !== '' && (fold(p.destination).includes(city) || city.includes(fold(p.destination)))
  return [...matches.filter(isHere), ...matches.filter((p) => !isHere(p))]
}

export function SavedPlaceFilters({
  query,
  destination,
  kind,
  destinations,
  onQuery,
  onDestination,
  onKind,
}: {
  query: string
  destination: string
  kind: string
  destinations: string[]
  onQuery(value: string): void
  onDestination(value: string): void
  onKind(value: string): void
}) {
  const t = useT()
  return (
    <div className="flex flex-col gap-2">
      <Input
        type="search"
        value={query}
        aria-label={t('savedPlaces.search')}
        placeholder={t('savedPlaces.search')}
        onChange={(event) => onQuery(event.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <Select
          aria-label={t('savedPlaces.filter.destination')}
          value={destination}
          onChange={(event) => onDestination(event.target.value)}
        >
          <option value="">{t('savedPlaces.filter.allDestinations')}</option>
          {/* Only the destinations she has actually used. A fixed list of
              countries would be mostly empty and would not contain "Toscana". */}
          {destinations.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('savedPlaces.filter.kind')}
          value={kind}
          onChange={(event) => onKind(event.target.value)}
        >
          <option value="">{t('savedPlaces.filter.allKinds')}</option>
          {SAVED_PLACE_KINDS.map((value) => (
            <option key={value} value={value}>
              {t(`savedPlaces.kind.${value}`)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  )
}

/** The distinct destinations in her library, for the filter. */
export function destinationsOf(all: SavedPlace[]): string[] {
  return [...new Set(all.map((p) => p.destination))].sort((a, b) => a.localeCompare(b))
}

/**
 * The compact picker, opened from inside a day.
 *
 * Adding from here puts the place ON that day. The first version could only add
 * to the trip, which dropped everything into "not yet scheduled" and left her to
 * move it — so the library was most useful exactly where it was least usable,
 * halfway through building Tuesday.
 */
export function SavedPlacePicker({
  places,
  here,
  busy,
  onPick,
  onCancel,
}: {
  places: SavedPlace[]
  here: string
  busy: boolean
  onPick(savedPlaceId: string): void
  onCancel(): void
}) {
  const t = useT()
  const [query, setQuery] = useState('')
  const [destination, setDestination] = useState('')
  const [kind, setKind] = useState('')

  const destinations = useMemo(() => destinationsOf(places), [places])
  const shown = useMemo(
    () => narrowSavedPlaces(places, { query, destination, kind, here }),
    [places, query, destination, kind, here],
  )

  return (
    <div
      data-testid="saved-place-picker"
      className="flex flex-col gap-3 rounded-md bg-slate-50 p-3"
    >
      {places.length === 0 ? (
        <p className="text-sm text-slate-500">{t('savedPlaces.empty')}</p>
      ) : (
        <>
          <SavedPlaceFilters
            query={query}
            destination={destination}
            kind={kind}
            destinations={destinations}
            onQuery={setQuery}
            onDestination={setDestination}
            onKind={setKind}
          />

          {shown.length === 0 ? (
            <p className="text-sm text-slate-500">{t('savedPlaces.noMatches')}</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
              {shown.map((place) => (
                <li key={place.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onPick(place.id)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-white disabled:opacity-50"
                  >
                    <span className="font-medium">{place.name}</span>
                    <span className="text-xs text-slate-500">
                      {t(`savedPlaces.kind.${place.kind}`)} · {place.destination}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div>
        <Button type="button" variant="quiet" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
